import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight, faXmark, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "../../Constant";

const CartTotal = ({
    cartUpdated = [],
    CartTotalText = "Proceed To Pay",
    selectedAddressId,
}) => {
    const location = useLocation();
    const navigation = useNavigate();
    const [couponDetail, setCouponDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showCoupon, setShowCoupon] = useState(true);

    // ✅ User or Guest Identifier
    const getApiIdentifier = useCallback(() => {
        const userId = localStorage.getItem('userId');
        const guestId = localStorage.getItem('guest_id');

        if (guestId && userId === guestId) {
            return { guest_id: guestId };
        }

        if (userId) {
            return { user_id: userId };
        }

        return null;
    }, []);

    const cartItems = cartUpdated || [];
    const total = cartItems.reduce((sum, item) => sum + (item.total || 0), 0);

    const handleContinue = async () => {
        if (location.pathname === "/shoping-cart") {
            navigation("/Billing-Address");
            return;
        }

        const identifier = getApiIdentifier();
        if (!identifier) {
            alert("Your session has expired. Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const finalAmount = total - (couponDetail?.discounted_amount || 0);

            const orderResponse = await axios.post(`${api}order_place`, {
                ...identifier,
                address_id: selectedAddressId,
                coupon_id: localStorage.getItem("coupon_id"),
                product_details: cartItems.map(item => ({
                    product_id: item.product_id,
                    quantity: item.selected_quantity,
                     product_amount: item.discount_price && item.discount_price > 0 
                ? item.discount_price 
                : item.price, // ✅ fallback to price if discount_price is 0
                    product_total: item.total,
                })),
                coupon_discount: couponDetail?.discounted_amount || 0,
                total: finalAmount,
            });

            if (orderResponse.data.success !== "true") {
                throw new Error(orderResponse.data.message || "Failed to create order.");
            }

            const { order_id: serverOrderId, razorpay_order_id } = orderResponse.data;
            const options = {
                key: "rzp_live_RSwLSAE4MVSJTz",
                // key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_vmxoAc8eMJjXS5",
                amount: Math.round(finalAmount * 100),
                currency: "INR",
                name: "Little Love Nature",
                description: `Order ID: ${serverOrderId}`,
                order_id: razorpay_order_id,
                handler: async function (paymentResponse) {
                    try {
                        const verificationResponse = await axios.post(`${api}order_place`, {
                            ...identifier,
                            payment_id: paymentResponse.razorpay_payment_id,
                            order_id: serverOrderId,
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                        });

                        if (verificationResponse.data.success === "true") {
                            navigation("/Order-success", { state: { orderId: serverOrderId } });
                        } else {
                            alert(verificationResponse.data.message || "Payment successful, but order confirmation failed.");
                        }
                    } catch (error) {
                        alert(error.response?.data?.message || "A server error occurred during payment verification.");
                    }
                },
                prefill: {
                    name: localStorage.getItem("userName") || 'Guest User',
                    email: localStorage.getItem("userEmail") || undefined,
                    contact: localStorage.getItem("userPhone") || undefined,
                },
                theme: { color: "#227070" },
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();

        } catch (error) {
            console.error("Order process error:", error);
            alert(error.message || "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    const coupannav = () => {
        navigation("/Coupouns");
        localStorage.setItem("totalamnt", total);
    };

    const removeCoupon = () => {
        setCouponDetail(null);
        setShowCoupon(true);
        localStorage.removeItem("coupon_id");
    };

    useEffect(() => {
        const couponId = localStorage.getItem("coupon_id");
        if (!total || !couponId) {
            setCouponDetail(null);
            setShowCoupon(true);
            return;
        }

        axios.post(`${api}Coupon_discount_details`, {
            total_amount: total,
            coupon_id: couponId
        })
        .then(response => {
            if (response.data.success === "true") {
                setCouponDetail(response.data.coupons);
                setShowCoupon(false);
            } else {
                removeCoupon();
            }
        })
        .catch(error => {
            console.error("Coupon fetch error:", error);
            removeCoupon();
        });
    }, [total, location.pathname]);

    const isButtonDisabled = isLoading || (location.pathname.includes("Billing-Address") && !selectedAddressId) || cartItems.length === 0;

    return (
        <div className="carttotal-bg">
            <div className="row">
                <div className="col-lg-12 title"><h2>CART TOTAL</h2></div>
            </div>
            <div className="row">
                <div className="col-lg-12 cart-total">
                    <div className="cart-itemcont cart-item-border">
                        <h6>Cart Items</h6>
                        <p>{cartItems.length}</p>
                    </div>

                    {cartItems.length === 0 ? (
                        <div className="no-items-in-cart"><p>No items in cart</p></div>
                    ) : (
                        cartItems.map((item) => (
                            <div key={item.product_id} className="cart-itemcont inner-b">
                                <h6>{item.name} ({item.selected_quantity})</h6>
                                <p>₹{item.total?.toFixed(2)}</p>
                            </div>
                        ))
                    )}

                    <div
                        className="cart-itemcont double-border"
                        onClick={showCoupon ? coupannav : undefined}
                        style={{ cursor: showCoupon ? 'pointer' : 'default' }}
                    >
                        {showCoupon ? (
                            <>
                                <h6>Select Coupon</h6>
                                <p><FontAwesomeIcon icon={faAngleRight} /></p>
                            </>
                        ) : (
                            <>
                                <div style={{ display: "flex", alignItems: "center", gap: '10px' }}>
                                    <h6>{couponDetail?.coupon_code}</h6>
                                    <div className="cancel-btncoupn" onClick={removeCoupon}>
                                        <FontAwesomeIcon icon={faXmark} />
                                    </div>
                                </div>
                                <p style={{ color: "#227070", fontWeight: "600" }}>
                                   -₹{Number(couponDetail?.discounted_amount ?? 0).toFixed(2)}
                                </p>
                            </>
                        )}
                    </div>

                    <div className="cart-itemcont inner-b total">
                        <h6>Total</h6>
                        <p>₹{(total - Number(couponDetail?.discounted_amount ?? 0)).toFixed(2)}</p>
                    </div>

                    <div className="cart-itemcont inner-b tatalcontinue">
                        <button onClick={handleContinue} disabled={isButtonDisabled}>
                            {isLoading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                                </>
                            ) : (
                                CartTotalText
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartTotal;
   